// Enhanced Role Management Types
// Supporting the new role management features

export interface RoleTemplate {
  id: string;
  name: string;
  description: string;
  category: 'music_industry' | 'technical' | 'business' | 'custom';
  permissions: string[];
  recommended_for: string[];
  icon: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface PermissionGroup {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  permissions: string[];
  is_core: boolean;
  dependencies?: string[];
  order: number;
}

export interface RoleHierarchyNode {
  role: Role;
  children: RoleHierarchyNode[];
  inherited_permissions: Permission[];
  direct_permissions: Permission[];
  user_count: number;
  level: number;
}

export interface RoleAnalytics {
  total_roles: number;
  active_roles: number;
  users_by_role: Record<string, number>;
  permission_coverage: number;
  most_used_permissions: Array<{
    permission_id: string;
    permission_name: string;
    usage_count: number;
  }>;
  least_used_permissions: Array<{
    permission_id: string;
    permission_name: string;
    usage_count: number;
  }>;
  role_distribution: Array<{
    role_name: string;
    user_count: number;
    percentage: number;
  }>;
  security_score: number;
  recommendations: string[];
}

export interface RoleUsageStats {
  role_id: string;
  role_name: string;
  user_count: number;
  permission_count: number;
  last_used: string;
  activity_score: number;
  security_risk_level: 'low' | 'medium' | 'high';
  optimization_suggestions: string[];
}

export interface PermissionUpdate {
  permission_id: string;
  action: 'grant' | 'revoke';
}

export interface RoleHierarchyUpdate {
  role_id: string;
  parent_role_id?: string;
  inheritance_type: 'additive' | 'restrictive';
}

export interface RoleFilters {
  search: string;
  category: string;
  permission_group: string;
  user_count_range: {
    min: number;
    max: number;
  };
  activity_level: 'high' | 'medium' | 'low' | 'inactive' | '';
  security_risk: 'low' | 'medium' | 'high' | '';
  template_based: boolean;
}

export interface BulkRoleOperation {
  type: 'permission_grant' | 'permission_revoke' | 'activate' | 'deactivate' | 'delete';
  role_ids: string[];
  permission_ids?: string[];
  parameters?: Record<string, any>;
}

export interface BulkRoleOperationResult {
  success_count: number;
  failure_count: number;
  errors: Array<{
    role_id: string;
    error: string;
  }>;
}

// Pre-defined role templates for the music industry
export const ROLE_TEMPLATES: Record<string, Omit<RoleTemplate, 'id' | 'created_at' | 'updated_at'>> = {
  label_executive: {
    name: 'Label Executive',
    description: 'Full access to label operations and artist management',
    category: 'music_industry',
    permissions: [
      'clients:manage',
      'artists:manage', 
      'campaigns:manage',
      'budgets:manage',
      'reports:read',
      'releases:manage'
    ],
    recommended_for: ['Record label executives', 'A&R directors', 'Label managers'],
    icon: '🎵',
    is_default: true
  },
  campaign_manager: {
    name: 'Campaign Manager',
    description: 'Manage marketing campaigns and track performance',
    category: 'music_industry',
    permissions: [
      'campaigns:manage',
      'artists:read',
      'budgets:read',
      'reports:read',
      'releases:read'
    ],
    recommended_for: ['Marketing managers', 'Campaign coordinators', 'Promotion specialists'],
    icon: '🎯',
    is_default: true
  },
  artist_manager: {
    name: 'Artist Manager',
    description: 'Manage artists and their releases',
    category: 'music_industry',
    permissions: [
      'artists:manage',
      'releases:manage',
      'campaigns:read',
      'reports:read',
      'budgets:read'
    ],
    recommended_for: ['Artist managers', 'Talent coordinators', 'Artist relations'],
    icon: '🎤',
    is_default: true
  },
  data_analyst: {
    name: 'Data Analyst',
    description: 'Access to analytics and reporting tools',
    category: 'business',
    permissions: [
      'reports:manage',
      'campaigns:read',
      'artists:read',
      'budgets:read',
      'releases:read'
    ],
    recommended_for: ['Data analysts', 'Business intelligence', 'Performance analysts'],
    icon: '📊',
    is_default: true
  },
  content_creator: {
    name: 'Content Creator',
    description: 'Create and manage content for artists and campaigns',
    category: 'music_industry',
    permissions: [
      'campaigns:create',
      'campaigns:update',
      'artists:read',
      'releases:read',
      'reports:read'
    ],
    recommended_for: ['Content creators', 'Social media managers', 'Creative directors'],
    icon: '🎨',
    is_default: false
  },
  financial_controller: {
    name: 'Financial Controller',
    description: 'Manage budgets and financial operations',
    category: 'business',
    permissions: [
      'budgets:manage',
      'campaigns:read',
      'reports:manage',
      'clients:read'
    ],
    recommended_for: ['Financial controllers', 'Budget managers', 'Accounting staff'],
    icon: '💰',
    is_default: false
  },
  system_admin: {
    name: 'System Administrator',
    description: 'Full system administration capabilities',
    category: 'technical',
    permissions: [
      'users:manage',
      'roles:manage',
      'system:configure',
      'integrations:manage',
      'reports:manage'
    ],
    recommended_for: ['System administrators', 'IT managers', 'Technical leads'],
    icon: '⚙️',
    is_default: false
  }
};

// Permission grouping for better organization
export const PERMISSION_GROUPS: Record<string, PermissionGroup> = {
  artist_management: {
    id: 'artist_management',
    name: 'Artist Management',
    description: 'Manage artists, their profiles, and related data',
    icon: '🎵',
    color: 'blue',
    permissions: ['artists:create', 'artists:read', 'artists:update', 'artists:delete', 'artists:manage'],
    is_core: true,
    order: 1
  },
  release_management: {
    id: 'release_management',
    name: 'Release Management',
    description: 'Manage music releases and distribution',
    icon: '💿',
    color: 'purple',
    permissions: ['releases:create', 'releases:read', 'releases:update', 'releases:delete', 'releases:manage'],
    is_core: true,
    order: 2
  },
  campaign_operations: {
    id: 'campaign_operations',
    name: 'Campaign Operations',
    description: 'Create and manage marketing campaigns',
    icon: '🎯',
    color: 'green',
    permissions: ['campaigns:create', 'campaigns:read', 'campaigns:update', 'campaigns:delete', 'campaigns:manage'],
    is_core: true,
    order: 3
  },
  financial_management: {
    id: 'financial_management',
    name: 'Financial Management',
    description: 'Manage budgets and financial data',
    icon: '💰',
    color: 'yellow',
    permissions: ['budgets:create', 'budgets:read', 'budgets:update', 'budgets:delete', 'budgets:manage', 'budgets:approve'],
    is_core: true,
    dependencies: ['campaign_operations'],
    order: 4
  },
  client_management: {
    id: 'client_management',
    name: 'Client Management',
    description: 'Manage client accounts and relationships',
    icon: '👥',
    color: 'indigo',
    permissions: ['clients:create', 'clients:read', 'clients:update', 'clients:delete', 'clients:manage'],
    is_core: true,
    order: 5
  },
  reporting_analytics: {
    id: 'reporting_analytics',
    name: 'Reporting & Analytics',
    description: 'Access reports and analytics data',
    icon: '📊',
    color: 'pink',
    permissions: ['reports:create', 'reports:read', 'reports:update', 'reports:delete', 'reports:manage', 'reports:export'],
    is_core: true,
    order: 6
  },
  user_administration: {
    id: 'user_administration',
    name: 'User Administration',
    description: 'Manage users and their access',
    icon: '👤',
    color: 'red',
    permissions: ['users:create', 'users:read', 'users:update', 'users:delete', 'users:manage', 'users:invite'],
    is_core: false,
    order: 7
  },
  role_management: {
    id: 'role_management',
    name: 'Role Management',
    description: 'Manage roles and permissions',
    icon: '🛡️',
    color: 'orange',
    permissions: ['roles:create', 'roles:read', 'roles:update', 'roles:delete', 'roles:manage'],
    is_core: false,
    dependencies: ['user_administration'],
    order: 8
  },
  system_administration: {
    id: 'system_administration',
    name: 'System Administration',
    description: 'System configuration and maintenance',
    icon: '⚙️',
    color: 'gray',
    permissions: ['system:configure', 'system:maintain', 'integrations:manage', 'integrations:configure'],
    is_core: false,
    dependencies: ['user_administration', 'role_management'],
    order: 9
  }
};

// Role category colors
export const ROLE_CATEGORY_COLORS = {
  music_industry: 'bg-blue-100 text-blue-800 border-blue-200',
  technical: 'bg-gray-100 text-gray-800 border-gray-200',
  business: 'bg-green-100 text-green-800 border-green-200',
  custom: 'bg-purple-100 text-purple-800 border-purple-200'
} as const;

// Permission action colors (consistent with existing system)
export const PERMISSION_ACTION_COLORS = {
  create: 'bg-green-100 text-green-800',
  read: 'bg-blue-100 text-blue-800',
  update: 'bg-yellow-100 text-yellow-800',
  delete: 'bg-red-100 text-red-800',
  manage: 'bg-purple-100 text-purple-800',
  approve: 'bg-indigo-100 text-indigo-800',
  export: 'bg-pink-100 text-pink-800',
  configure: 'bg-orange-100 text-orange-800',
  invite: 'bg-teal-100 text-teal-800',
  maintain: 'bg-cyan-100 text-cyan-800'
} as const;

// Security risk level colors
export const SECURITY_RISK_COLORS = {
  low: 'bg-green-100 text-green-800 border-green-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  high: 'bg-red-100 text-red-800 border-red-200'
} as const;

// Activity level colors
export const ACTIVITY_LEVEL_COLORS = {
  high: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-orange-100 text-orange-800',
  inactive: 'bg-gray-100 text-gray-800'
} as const;

// Helper functions
export const getPermissionGroupColor = (groupId: string): string => {
  const group = PERMISSION_GROUPS[groupId];
  return group ? `bg-${group.color}-100 text-${group.color}-800` : 'bg-gray-100 text-gray-800';
};

export const getPermissionActionColor = (action: string): string => {
  return PERMISSION_ACTION_COLORS[action as keyof typeof PERMISSION_ACTION_COLORS] || 'bg-gray-100 text-gray-800';
};

export const getRoleCategoryColor = (category: string): string => {
  return ROLE_CATEGORY_COLORS[category as keyof typeof ROLE_CATEGORY_COLORS] || ROLE_CATEGORY_COLORS.custom;
};

export const getSecurityRiskColor = (risk: string): string => {
  return SECURITY_RISK_COLORS[risk as keyof typeof SECURITY_RISK_COLORS] || SECURITY_RISK_COLORS.low;
};

export const getActivityLevelColor = (level: string): string => {
  return ACTIVITY_LEVEL_COLORS[level as keyof typeof ACTIVITY_LEVEL_COLORS] || ACTIVITY_LEVEL_COLORS.inactive;
};

// Import existing types
import type { Role, Permission } from './auth';
